import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useUserLogged, useOrgUuid } from '@/hooks/useUserLogged';
import { SEATS_ONLY_PARAM, resolveSeatsOnly } from '@/hooks/useLeaderboard';
import {
  agentInsightsMemberDetailKey,
  agentInsightsMembersKey,
  agentInsightsOverviewBundleKey,
} from '@/lib/queryKeys/agentInsights';
import { useAgentInsightsTeamUuid } from '@/hooks/useAgentInsightsTeam';
import {
  fetchTeamInsightOverview,
  fetchCursorTeamInsightOverview,
  fetchTeamInsightPerMember,
  fetchCursorTeamInsightActivity,
  fetchCursorTeamInsightMetrics,
  fetchCursorTeamInsightOutput,
  fetchTeamInsightCoreAnalytics,
  fetchTeamInsightCoreAnalyticsTeams,
  fetchTeamInsightCoreAnalyticsTeamChart,
  fetchTeamInsightCoreAnalyticsEngineerChart,
  fetchTeamInsightCodeReviewAnalytics,
  fetchTeamInsightCodeReviewAnalyticsMetrics,
  fetchTeamInsightCodeReviewAnalyticsOutputTypeChart,
  fetchTeamInsightExternalAnalytics,
  fetchTeamInsightExternalAnalyticsMetrics,
  fetchTeamInsightExternalAnalyticsRevertedPRs,
  fetchTeamInsightExternalAnalyticsCodeTurnover,
  fetchTeamInsightExternalAnalyticsBugReports,
  fetchCostCharts,
  fetchWorkTypePerPerson,
  fetchAIUsageStatsTable,
  fetchTeamPlanUsage,
  fetchAgentInsightsMemberDetail,
  AgentInsightsMemberDetailResponse,
  fetchAgentInsightsMemberBrief,
  AgentInsightsMemberBriefResponse,
  fetchAgentInsightsMemberSyncHealth,
  AgentInsightsMemberSyncHealthResponse,
  fetchAgentInsightsMemberReprocessPreview,
  MemberReprocessPreviewResponse,
  fetchAgentInsightsMemberReprocess,
  MemberReprocessResponse,
  fetchAgentInsightsTeamStatus,
  AgentInsightsTeamStatusResponse,
  fetchAgentInsightsOnboardingStatus,
  fetchAgentInsightsDataStatus,
  AgentInsightsDataStatusResponse,
  fetchModelRouterOnboardingStatus,
  fetchModelRouterDashboard,
  type ModelRouterRange,
  type ModelRouterGranularity,
  fetchModelRouterSettings,
  fetchModelRouterSessions,
  fetchModelRouterMembers,
  fetchModelRouterMemberDetail,
  fetchAgentInsightsCoaching,
  AgentInsightsCoachingResponse,
  fetchAgentSessions,
  fetchAgentSessionDetail,
  AgentSessionDetail,
  AgentSessionsResponse,
  fetchAgentSessionActivityMap,
  AgentSessionActivityMapParams,
  AgentSessionActivityMapResponse,
  AgentInsightsTeamHealthResponse,
  fetchAgentInsightsOverviewBundle,
  fetchAgentInsightsOverviewSection,
  fetchAgentInsightsMembers,
  AgentInsightsOverviewBundleResponse,
  AgentInsightsOverviewBundleSections,
  composeMaturityFromBundle,
  composeTeamHealthFromBundle,
  AgentMaturityResponse,
  fetchAgentInsightsFindings,
  fetchModelQualityComparison,
  ModelQualityComparisonParams,
  ModelQualityComparisonResponse,
  isAccessDenied,
  TeamInsightOverviewRequest,
  AIUsageStatsTableRequest,
  TeamInsightPerMemberRequest,
  TeamInsightCoreAnalyticsTeamsResponse,
  TeamInsightCodeReviewAnalyticsMetricsResponse,
  TeamInsightExternalAnalyticsMetricsResponse,
  CursorTeamInsightMetricsResponse,
  CostChartsResponse,
  TeamInsightWorkTypePerPersonResponse,
  TeamPlanUsageResponse,
} from '@/apis/teamInsightsOverview';
import {
  SessionExplorerError,
  fetchSessionExplorerMapUrls,
  fetchSessionExplorerSessions,
  fetchSessionExplorerStatus,
  fetchSessionMapDocument,
} from '@/apis/teamInsightsOverview';
import {
  isMapFound,
  type PendingReview,
  type SessionExplorerMapBatch,
  type SessionExplorerMapMeta,
  type SessionMapAnnotation,
} from '@/apis/sessionExplorerSchema';
import { fetchMapWithRetry, mapRequestIds } from '@/apis/sessionExplorerMaps';
import {
  fetchDayDrillDown,
  DayDrillDownResponse,
} from '@/apis/codingAgentDayDrillDown';

/** Query key prefixes for all team insights metrics. Invalidate these when backfill completes so fresh data is fetched. */
export const TEAM_INSIGHTS_QUERY_KEY_PREFIXES = [
  'teamInsightsOverview',
  'cursorTeamInsightsOverview',
  'teamInsightPerMember',
  'aiTeamInsightActivity',
  'aiTeamInsightMetrics',
  'aiTeamInsightOutput',
  'teamInsightCoreAnalytics',
  'teamInsightCoreAnalyticsTeams',
  'teamInsightCoreAnalyticsTeamChart',
  'teamInsightCoreAnalyticsEngineerChart',
  'teamInsightCodeReviewAnalytics',
  'teamInsightCodeReviewAnalyticsMetrics',
  'teamInsightCodeReviewAnalyticsQualityChart',
  'teamInsightCodeReviewAnalyticsTurnaroundChart',
  'teamInsightCodeReviewAnalyticsOutputTypeChart',
  'teamInsightCodeReviewAnalyticsDepthChart',
  'teamInsightExternalAnalytics',
  'teamInsightExternalAnalyticsMetrics',
  'teamInsightExternalAnalyticsRevertedPRs',
  'teamInsightExternalAnalyticsCodeTurnover',
  'teamInsightExternalAnalyticsBugReports',
  'teamInsightMetricsSummary',
  'costCharts',
  'workTypePerPerson',
  'aiUsageStatsTable',
  // Agent Insights tab — synced by the `entelligence insights` CLI, also
  // invalidated on backfill completion so the tab doesn't keep serving stale data.
  'agentInsightsOverviewBundle',
  'agentInsightsOverviewSection',
  'agentInsightsMembers',
  'agentInsightsFindings',
  'agentSessionActivityMap',
  'agentInsightsOnboardingStatus',
  'codingAgentDayDrillDown',
  'skillRecommendations',
  'skillUsage',
  'mcpUsage',
  'skillOutcomes',
  'skill-metrics',
  // Knowledge Graph tab (memory-graph explorer, single-org alpha).
  'knowledgeGraphPlane',
  'knowledgeGraphPatterns',
  'knowledgeGraphSearch',
  'knowledgeGraphNeighborhood',
  'knowledgeGraphNode',
  // Complementary caches that should refresh alongside team insights data.
  'cursorMappings',
  'claudeCodeMappings',
  'aiSourcesConfig',
  'deploymentMetrics',
] as const;

// A gateway timeout (504) or bad gateway (502) means the request already burned its
// full wait budget upstream (the BFF proxy aborts slow team-insight calls at 170s and
// returns a clean error) -- retrying just repeats the same multi-minute wait. Genuine
// network blips (fetch failures, broken pipes) still retry. Match case-insensitively:
// thrown messages read "504 Gateway Timeout" (capital T), which `.includes('timeout')`
// silently missed.
const isNonRetryableUpstreamError = (error: any): boolean =>
  /timeout|broken pipe|\b(502|504)\b/i.test(String(error?.message ?? ''));

// Helper function to normalize payload for consistent queryKey matching across tabs
const normalizePayload = (
  payload: TeamInsightOverviewRequest
): TeamInsightOverviewRequest => {
  return {
    ...payload,
    // Sort team_uuids array to ensure consistent queryKey matching
    team_uuids: payload.team_uuids ? [...payload.team_uuids].sort() : undefined,
    // Ensure date_interval is consistently structured
    date_interval: payload.date_interval
      ? {
          start_date: payload.date_interval.start_date,
          end_date: payload.date_interval.end_date,
        }
      : undefined,
  };
};

/**
 * Adds Team Insights' "Paid seats only" scope to a request payload.
 *
 * Reads the same `?seats=paid` URL param the leaderboard's <PaidSeatsFilter />
 * writes, via the same `resolveSeatsOnly` predicate, so the two surfaces can
 * never disagree about whether the filter is on. The URL rather than component
 * state because a Team Insights tab renders many independent callers of these
 * hooks — one param means the charts on screen always describe the same
 * population, where prop-threading would leave any component someone forgot to
 * update rendering unfiltered numbers beside filtered ones.
 *
 * The returned object flows into each hook's queryKey, so filtered and
 * unfiltered results are cached separately and toggling refetches rather than
 * showing the other population's cached data.
 *
 * ONLY applied to hooks whose endpoint actually honours `seats_only` on the
 * backend. Sending it to an endpoint that ignores it would render whole-org
 * numbers under a "paid seats" label, which is the exact misreporting this
 * feature exists to remove.
 */
const useSeatScopedPayload = <T extends { org_uuid?: string }>(
  payload: T
): T & { seats_only?: boolean } => {
  const searchParams = useSearchParams();
  const seatsOnly = resolveSeatsOnly({
    orgUUID: payload.org_uuid,
    seatsParam: searchParams?.get(SEATS_ONLY_PARAM),
  });
  return seatsOnly ? { ...payload, seats_only: true } : payload;
};

export const useTeamInsightsOverview = (
  payload: TeamInsightOverviewRequest
) => {
  // Check if we have at least one valid identifier
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['teamInsightsOverview', payload],
    queryFn: async () => {
      return await fetchTeamInsightOverview(payload);
    },
    enabled: hasValidIdentifier,
    // Optimized caching strategy
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer (renamed from cacheTime in v5)
    refetchInterval: 10 * 60 * 1000, // 10 minutes - less frequent refetch
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch if data is fresh
    retry: 2, // Retry failed requests 2 times
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

export const useCursorTeamInsightsOverview = (
  payload: TeamInsightOverviewRequest
) => {
  // Check if we have at least one valid identifier
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['cursorTeamInsightsOverview', payload],
    queryFn: async () => {
      return await fetchCursorTeamInsightOverview(payload);
    },
    enabled: hasValidIdentifier,
    // Optimized caching strategy
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer (renamed from cacheTime in v5)
    refetchInterval: 10 * 60 * 1000, // 10 minutes - less frequent refetch
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch if data is fresh
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    // Add network error handling
    networkMode: 'online',
    // Better error handling
    throwOnError: false, // Don't throw errors, let components handle them
  });
};

export const useTeamInsightPerMember = (
  payload: TeamInsightPerMemberRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  // Check if we have at least one valid identifier
  const hasValidIdentifier = !!seatScoped.org_uuid;

  return useQuery({
    queryKey: ['teamInsightPerMember', seatScoped],
    queryFn: async () => {
      return await fetchTeamInsightPerMember(seatScoped);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    // Optimized caching strategy
    staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
    refetchInterval: 5 * 60 * 1000, // 5 minutes - refetch interval
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch if data is fresh
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    // Add network error handling
    networkMode: 'online',
    // Better error handling
    throwOnError: false, // Don't throw errors, let components handle them
  });
};

// New split cursor endpoint hooks
export const useCursorTeamInsightActivity = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['aiTeamInsightActivity', payload],
    queryFn: async () => {
      return await fetchCursorTeamInsightActivity(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 3 * 60 * 1000, // 3 minutes - data stays fresh
    gcTime: 8 * 60 * 1000, // 8 minutes - keep in cache
    // No polling: this endpoint runs 5+ minutes server-side right now (backend fix
    // in progress). An 8-minute interval on an idle tab would restart that whole
    // multi-minute wait forever. Data still refreshes on remount / explicit refetch.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useCursorTeamInsightMetrics = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['aiTeamInsightMetrics', payload],
    queryFn: async () => {
      return await fetchCursorTeamInsightMetrics(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 3 * 60 * 1000, // 3 minutes - data stays fresh
    gcTime: 8 * 60 * 1000, // 8 minutes - keep in cache
    // No polling: this endpoint runs 5+ minutes server-side right now (backend fix
    // in progress). An 8-minute interval on an idle tab would restart that whole
    // multi-minute wait forever. Data still refreshes on remount / explicit refetch.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useCursorTeamInsightOutput = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['aiTeamInsightOutput', payload],
    queryFn: async () => {
      return await fetchCursorTeamInsightOutput(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 3 * 60 * 1000, // 3 minutes - data stays fresh
    gcTime: 8 * 60 * 1000, // 8 minutes - keep in cache
    // No polling: this endpoint runs 5+ minutes server-side right now (backend fix
    // in progress). An 8-minute interval on an idle tab would restart that whole
    // multi-minute wait forever. Data still refreshes on remount / explicit refetch.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// New optimized endpoint hooks
export const useTeamInsightCoreAnalytics = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;

  return useQuery({
    queryKey: ['teamInsightCoreAnalytics', seatScoped],
    queryFn: async () => {
      return await fetchTeamInsightCoreAnalytics(seatScoped);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// Split core analytics hooks (parallel loading)
export const useTeamInsightCoreAnalyticsTeams = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;

  return useQuery({
    queryKey: ['teamInsightCoreAnalyticsTeams', seatScoped],
    queryFn: async () => {
      return await fetchTeamInsightCoreAnalyticsTeams(seatScoped);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightCoreAnalyticsTeamChart = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;

  return useQuery({
    queryKey: ['teamInsightCoreAnalyticsTeamChart', seatScoped],
    queryFn: async () => {
      return await fetchTeamInsightCoreAnalyticsTeamChart(seatScoped);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightCoreAnalyticsEngineerChart = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;

  return useQuery({
    queryKey: ['teamInsightCoreAnalyticsEngineerChart', seatScoped],
    queryFn: async () => {
      return await fetchTeamInsightCoreAnalyticsEngineerChart(seatScoped);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 60 * 1000, // 1 minute — scores written async by on-merge daemon; short stale time ensures tooltip reflects them quickly
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // refetch on navigation so on-merge daemon score writes become visible in the Output per Engineer tooltip
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// TODO: delete. split into many. confirm new ones working
export const useTeamInsightCodeReviewAnalytics = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['teamInsightCodeReviewAnalytics', payload],
    queryFn: async () => {
      return await fetchTeamInsightCodeReviewAnalytics(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// Split code review analytics hooks (parallel loading)
export const useTeamInsightCodeReviewAnalyticsMetrics = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;
  const normalizedPayload = normalizePayload(seatScoped);

  return useQuery({
    queryKey: ['teamInsightCodeReviewAnalyticsMetrics', normalizedPayload],
    queryFn: async () => {
      return await fetchTeamInsightCodeReviewAnalyticsMetrics(
        normalizedPayload
      );
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightCodeReviewAnalyticsQualityChart = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;
  const normalizedPayload = normalizePayload(seatScoped);
  const queryClient = useQueryClient();

  // Use cached data from metrics hook if available (optimization)
  const metricsQueryKey = [
    'teamInsightCodeReviewAnalyticsMetrics',
    normalizedPayload,
  ];

  return useQuery({
    queryKey: ['teamInsightCodeReviewAnalyticsQualityChart', normalizedPayload],
    queryFn: async () => {
      // Try to get cached metrics data first
      const cachedMetrics = queryClient.getQueryData(metricsQueryKey) as any;
      if (cachedMetrics?.code_review_quality_chart) {
        return {
          org_uuid: cachedMetrics.org_uuid,
          code_review_quality_chart: cachedMetrics.code_review_quality_chart,
          date_range: cachedMetrics.date_range,
          error: cachedMetrics.error,
        };
      }
      // Fetch via queryClient so we join the in-flight metrics promise
      // instead of making a duplicate API call.
      const fetched = await queryClient.fetchQuery({
        queryKey: metricsQueryKey,
        queryFn: () =>
          fetchTeamInsightCodeReviewAnalyticsMetrics(normalizedPayload),
        staleTime: 5 * 60 * 1000,
      });
      return {
        org_uuid: fetched.org_uuid,
        code_review_quality_chart: fetched.code_review_quality_chart,
        date_range: fetched.date_range,
        error: (fetched as any).error,
      };
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightCodeReviewAnalyticsTurnaroundChart = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;
  const normalizedPayload = normalizePayload(seatScoped);
  const queryClient = useQueryClient();

  // Use cached data from metrics hook if available (optimization)
  const metricsQueryKey = [
    'teamInsightCodeReviewAnalyticsMetrics',
    normalizedPayload,
  ];

  return useQuery({
    queryKey: [
      'teamInsightCodeReviewAnalyticsTurnaroundChart',
      normalizedPayload,
    ],
    queryFn: async () => {
      // Try to get cached metrics data first
      const cachedMetrics = queryClient.getQueryData(metricsQueryKey) as any;
      if (cachedMetrics?.code_review_turnaround_chart) {
        return {
          org_uuid: cachedMetrics.org_uuid,
          code_review_turnaround_chart:
            cachedMetrics.code_review_turnaround_chart,
          date_range: cachedMetrics.date_range,
          error: cachedMetrics.error,
        };
      }
      // Fetch via queryClient so we join the in-flight metrics promise
      const fetched = await queryClient.fetchQuery({
        queryKey: metricsQueryKey,
        queryFn: () =>
          fetchTeamInsightCodeReviewAnalyticsMetrics(normalizedPayload),
        staleTime: 5 * 60 * 1000,
      });
      return {
        org_uuid: fetched.org_uuid,
        code_review_turnaround_chart: fetched.code_review_turnaround_chart,
        date_range: fetched.date_range,
        error: (fetched as any).error,
      };
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightCodeReviewAnalyticsOutputTypeChart = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;
  // The endpoint ignores `integration`, so the Cursor and Codex callers on one
  // page share a single request instead of fetching the same chart twice.
  const { integration: _integration, ...normalizedPayload } =
    normalizePayload(seatScoped);

  // This chart hits a dedicated endpoint (not shared with the metrics endpoint
  // like Quality/Turnaround/Depth), so it cannot share the metrics cache.
  return useQuery({
    queryKey: [
      'teamInsightCodeReviewAnalyticsOutputTypeChart',
      normalizedPayload,
    ],
    queryFn: async () => {
      return await fetchTeamInsightCodeReviewAnalyticsOutputTypeChart(
        normalizedPayload
      );
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightCodeReviewAnalyticsDepthChart = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;
  const normalizedPayload = normalizePayload(seatScoped);
  const queryClient = useQueryClient();

  // Use cached data from metrics hook if available (optimization)
  const metricsQueryKey = [
    'teamInsightCodeReviewAnalyticsMetrics',
    normalizedPayload,
  ];

  return useQuery({
    queryKey: ['teamInsightCodeReviewAnalyticsDepthChart', normalizedPayload],
    queryFn: async () => {
      // Try to get cached metrics data first
      const cachedMetrics = queryClient.getQueryData(metricsQueryKey) as any;
      if (cachedMetrics?.code_review_depth_chart) {
        return {
          org_uuid: cachedMetrics.org_uuid,
          code_review_depth_chart: cachedMetrics.code_review_depth_chart,
          date_range: cachedMetrics.date_range,
          error: cachedMetrics.error,
        };
      }
      // Fetch via queryClient so we join the in-flight metrics promise
      const fetched = await queryClient.fetchQuery({
        queryKey: metricsQueryKey,
        queryFn: () =>
          fetchTeamInsightCodeReviewAnalyticsMetrics(normalizedPayload),
        staleTime: 5 * 60 * 1000,
      });
      return {
        org_uuid: fetched.org_uuid,
        code_review_depth_chart: fetched.code_review_depth_chart,
        date_range: fetched.date_range,
        error: (fetched as any).error,
      };
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightExternalAnalytics = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['teamInsightExternalAnalytics', payload],
    queryFn: async () => {
      return await fetchTeamInsightExternalAnalytics(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// Split external analytics hooks (parallel loading)
export const useTeamInsightExternalAnalyticsMetrics = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;
  const normalizedPayload = normalizePayload(payload);

  return useQuery({
    queryKey: ['teamInsightExternalAnalyticsMetrics', normalizedPayload],
    queryFn: async () => {
      return await fetchTeamInsightExternalAnalyticsMetrics(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightExternalAnalyticsRevertedPRs = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;
  const normalizedPayload = normalizePayload(payload);

  return useQuery({
    queryKey: ['teamInsightExternalAnalyticsRevertedPRs', normalizedPayload],
    queryFn: async () => {
      return await fetchTeamInsightExternalAnalyticsRevertedPRs(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightExternalAnalyticsCodeTurnover = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;
  const normalizedPayload = normalizePayload(payload);

  return useQuery({
    queryKey: ['teamInsightExternalAnalyticsCodeTurnover', normalizedPayload],
    queryFn: async () => {
      return await fetchTeamInsightExternalAnalyticsCodeTurnover(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

export const useTeamInsightExternalAnalyticsBugReports = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;
  const normalizedPayload = normalizePayload(payload);

  return useQuery({
    queryKey: ['teamInsightExternalAnalyticsBugReports', normalizedPayload],
    queryFn: async () => {
      return await fetchTeamInsightExternalAnalyticsBugReports(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// Optimized metrics summary hook - reuses cached data from other queries
// This avoids duplicate API calls by aggregating data from already-fetched queries
export const useTeamInsightMetricsSummary = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;
  const normalizedPayload = normalizePayload(payload);
  const queryClient = useQueryClient();
  const isEnabled = hasValidIdentifier && additionalEnabled;

  // Use the same query keys as the individual hooks to access their cached data
  // Note: Some hooks use normalizedPayload, others use payload directly - match exactly
  const coreAnalyticsQueryKey = ['teamInsightCoreAnalyticsTeams', payload]; // Uses payload, not normalized
  const codeReviewQueryKey = [
    'teamInsightCodeReviewAnalyticsMetrics',
    normalizedPayload,
  ]; // Uses normalized
  const externalQueryKey = [
    'teamInsightExternalAnalyticsMetrics',
    normalizedPayload,
  ]; // Uses normalized
  const aiMetricsQueryKey = ['aiTeamInsightMetrics', payload]; // Uses payload, not normalized

  return useQuery({
    queryKey: ['teamInsightMetricsSummary', normalizedPayload],
    queryFn: async () => {
      // Try to get cached data from other queries first (avoids duplicate API calls)
      const [coreData, codeReviewData, externalData, aiData]: [
        TeamInsightCoreAnalyticsTeamsResponse,
        TeamInsightCodeReviewAnalyticsMetricsResponse,
        TeamInsightExternalAnalyticsMetricsResponse,
        CursorTeamInsightMetricsResponse,
      ] = await Promise.all([
        // Check cache first, then fetch if not available
        (async (): Promise<TeamInsightCoreAnalyticsTeamsResponse> => {
          const cached =
            queryClient.getQueryData<TeamInsightCoreAnalyticsTeamsResponse>(
              coreAnalyticsQueryKey
            );
          if (cached) {
            console.log('[Metrics Summary] Using cached core analytics data');
            return cached;
          }
          // If not cached, fetch it (will be cached for future use)
          return await queryClient.fetchQuery<TeamInsightCoreAnalyticsTeamsResponse>(
            {
              queryKey: coreAnalyticsQueryKey,
              queryFn: () => fetchTeamInsightCoreAnalyticsTeams(payload),
              staleTime: 5 * 60 * 1000,
            }
          );
        })(),
        (async (): Promise<TeamInsightCodeReviewAnalyticsMetricsResponse> => {
          const cached =
            queryClient.getQueryData<TeamInsightCodeReviewAnalyticsMetricsResponse>(
              codeReviewQueryKey
            );
          if (cached) {
            console.log('[Metrics Summary] Using cached code review data');
            return cached;
          }
          return await queryClient.fetchQuery<TeamInsightCodeReviewAnalyticsMetricsResponse>(
            {
              queryKey: codeReviewQueryKey,
              queryFn: () =>
                fetchTeamInsightCodeReviewAnalyticsMetrics(payload),
              staleTime: 5 * 60 * 1000,
            }
          );
        })(),
        (async (): Promise<TeamInsightExternalAnalyticsMetricsResponse> => {
          const cached =
            queryClient.getQueryData<TeamInsightExternalAnalyticsMetricsResponse>(
              externalQueryKey
            );
          if (cached) {
            console.log(
              '[Metrics Summary] Using cached external analytics data'
            );
            return cached;
          }
          return await queryClient.fetchQuery<TeamInsightExternalAnalyticsMetricsResponse>(
            {
              queryKey: externalQueryKey,
              queryFn: () => fetchTeamInsightExternalAnalyticsMetrics(payload),
              staleTime: 5 * 60 * 1000,
            }
          );
        })(),
        (async (): Promise<CursorTeamInsightMetricsResponse> => {
          const cached =
            queryClient.getQueryData<CursorTeamInsightMetricsResponse>(
              aiMetricsQueryKey
            );
          if (cached) {
            console.log('[Metrics Summary] Using cached AI metrics data');
            return cached;
          }
          return await queryClient.fetchQuery<CursorTeamInsightMetricsResponse>(
            {
              queryKey: aiMetricsQueryKey,
              queryFn: () => fetchCursorTeamInsightMetrics(payload),
              staleTime: 5 * 60 * 1000,
            }
          );
        })(),
      ]);

      // Aggregate into metrics summary format (4 daily endpoints + combine; faster than single summary endpoint)
      return {
        org_uuid: payload.org_uuid,
        date_range: payload.date_range,
        team_performance: {
          value: coreData?.overall_team_performance_average ?? 0,
          comparison_text: coreData?.overall_comparison_text ?? null,
        },
        review_quality: {
          value: codeReviewData?.overall_avg_review_quality ?? 0,
          comparison_text: codeReviewData?.quality_comparison_text ?? null,
        },
        review_turnaround: {
          value: codeReviewData?.overall_median_review_turnaround ?? 0,
          comparison_text: codeReviewData?.turnaround_comparison_text ?? null,
        },
        innovation_ratio: {
          value: externalData?.overall_innovation_ratio ?? 0,
          comparison_text:
            externalData?.innovation_ratio_comparison_text ?? null,
        },
        bug_ratio: {
          value: externalData?.overall_bug_ratio ?? 0,
          comparison_text: externalData?.bug_ratio_comparison_text ?? null,
          trend: externalData?.overall_bug_ratio_trend ?? null,
        },
        ai_adoption: {
          value: aiData?.ai_adoption ?? 0,
          comparison_text: aiData?.ai_adoption_comparison_text ?? null,
        },
        ai_savings: {
          value: aiData?.overall_ai_savings ?? 0,
          comparison_text: aiData?.ai_savings_comparison_text ?? null,
        },
        error: null,
      };
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// Cost Charts hook
export const useCostCharts = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery<CostChartsResponse>({
    queryKey: ['costCharts', payload],
    queryFn: async () => {
      return await fetchCostCharts(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// Work Type Per Person hook
export const useWorkTypePerPerson = (
  payload: TeamInsightOverviewRequest,
  additionalEnabled: boolean = true
) => {
  const seatScoped = useSeatScopedPayload(payload);

  const hasValidIdentifier = !!seatScoped.org_uuid;

  return useQuery<TeamInsightWorkTypePerPersonResponse>({
    queryKey: ['workTypePerPerson', seatScoped],
    queryFn: async () => {
      return await fetchWorkTypePerPerson(seatScoped);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 60 * 1000, // 1 minute — scores written async (on-merge daemon)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // refetch on navigation so async score writes become visible
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// ── AI Usage Stats Table ──

export const useAIUsageStatsTable = (
  payload: AIUsageStatsTableRequest,
  additionalEnabled: boolean = true
) => {
  const hasValidIdentifier = !!payload.org_uuid;

  return useQuery({
    queryKey: ['aiUsageStatsTable', payload],
    queryFn: async () => {
      return await fetchAIUsageStatsTable(payload);
    },
    enabled: hasValidIdentifier && additionalEnabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: any) => {
      // Don't retry on timeout, gateway (502/504), or broken pipe errors
      if (isNonRetryableUpstreamError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: 'online',
    throwOnError: false,
  });
};

// ── Claude Code Teams Plan (OTel) ──────────────────────────────────────

export function useTeamPlanUsage(
  orgUuid: string,
  startDate: string,
  endDate: string,
  enabled = true
) {
  /** React Query hook for per-user Claude Code usage from OTel DynamoDB. */
  return useQuery<TeamPlanUsageResponse>({
    queryKey: ['teamPlanUsage', orgUuid, startDate, endDate],
    queryFn: () => fetchTeamPlanUsage(orgUuid, startDate, endDate),
    enabled: enabled && !!orgUuid && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
  });
}

/** Shared by the hook and the member-card hover prefetch (one cache entry). */
export const agentInsightsMemberDetailQueryOptions = (
  orgUuid: string,
  userUuid: string,
  days?: number | null
) =>
  queryOptions<AgentInsightsMemberDetailResponse>({
    queryKey: agentInsightsMemberDetailKey(orgUuid, userUuid, days),
    queryFn: () => fetchAgentInsightsMemberDetail(orgUuid, userUuid, days),
    staleTime: 5 * 60 * 1000,
  });

export function useAgentInsightsMemberDetail(
  orgUuid: string,
  userUuid: string | null,
  enabled = true,
  days?: number | null
) {
  /** Per-engineer drill-down for the admin member-detail modal — peer bands,
   * activity heatmap, model split, cached coaching, and recent sessions. Only
   * fetches once a member is selected (userUuid set + enabled). `days`
   * windows member metrics, including model usage and costs. */
  return useQuery({
    ...agentInsightsMemberDetailQueryOptions(orgUuid, userUuid as string, days),
    enabled: enabled && !!orgUuid && !!userUuid,
  });
}

/** Patterns chart "explain" drill-down (ENT-4525) -- one member's per-session
 * and per-request billing rows for one UTC day. Only fetches while a day is
 * actually selected (the drill-down panel is open), so hovering the chart
 * never triggers a request -- only clicking a point does. */
export function useDayDrillDown(
  userUuid: string | null,
  day: string | null,
  enabled: boolean
) {
  /** orgUuid is baked into the key so the same userUuid/day seen from two
   *  different orgs (e.g. an admin bouncing between org contexts) never
   *  returns cached data from the wrong tenant -- the API itself derives org
   *  from the auth token, so nothing in the request otherwise disambiguates.
   *  `useOrgUuid` (not `organization?.OrgUUID ?? user?.OrganizationUUID ?? ''`
   *  -- see that hook's doc) so a soft-failed org fetch doesn't wedge this
   *  query permanently disabled behind an empty-string sentinel. */
  const orgUuid = useOrgUuid();
  return useQuery<DayDrillDownResponse>({
    queryKey: ['codingAgentDayDrillDown', orgUuid, userUuid, day],
    queryFn: () => fetchDayDrillDown(userUuid as string, day as string),
    enabled: enabled && !!orgUuid && !!userUuid && !!day,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgentInsightsMemberBrief(
  orgUuid: string,
  userUuid: string | null,
  enabled = true
) {
  /** One developer's nightly Member Brief (ENT-4198/ENT-4304) — work themes,
   * anomalies, and suggested prompts computed by the Stage-2 worker once a
   * night. A long staleTime avoids re-fetching an unchanged brief on every
   * tab switch. */
  return useQuery<AgentInsightsMemberBriefResponse>({
    queryKey: ['agentInsightsMemberBrief', orgUuid, userUuid],
    queryFn: () => fetchAgentInsightsMemberBrief(orgUuid, userUuid as string),
    enabled: enabled && !!orgUuid && !!userUuid,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAgentInsightsMemberSyncHealth(
  orgUuid: string,
  userUuid: string | null,
  startDate: string,
  endDate: string,
  enabled = true
) {
  /** Admin-only per-developer sync-health ledger (ENT-4197/ENT-4304). Gate
   * `enabled` on isAdmin at the call site to skip the 403 round trip
   * entirely for a non-admin viewer of their own page.
   *
   * The backend derives org from the authenticated caller, not a request
   * param, but `orgUuid` is still a required CACHE dimension: without it,
   * switching active org while userUuid/dates stay the same lets React
   * Query reuse the prior org's cached ledger instead of refetching,
   * showing stale tenant data (entloop-caught, MAJOR). */
  return useQuery<AgentInsightsMemberSyncHealthResponse>({
    queryKey: [
      'agentInsightsMemberSyncHealth',
      orgUuid,
      userUuid,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchAgentInsightsMemberSyncHealth(
        userUuid as string,
        startDate,
        endDate
      ),
    enabled: enabled && !!orgUuid && !!userUuid && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMemberReprocessPreview() {
  /** Blast-radius preview for the Data Health card's "Re-sync missing
   * window" button (ENT-4197) -- read-only, fires nothing. */
  return useMutation<
    MemberReprocessPreviewResponse,
    Error,
    { userUuid: string; resumeFrom: string; resumeTo: string }
  >({
    mutationFn: ({ userUuid, resumeFrom, resumeTo }) =>
      fetchAgentInsightsMemberReprocessPreview(userUuid, resumeFrom, resumeTo),
  });
}

export function useMemberReprocess() {
  /** Fires the per-client date-window reprocess directive (ENT-4197). On
   * success, invalidate this member's sync-health so the ledger reflects
   * the new directive without a full page reload. */
  const queryClient = useQueryClient();
  return useMutation<
    MemberReprocessResponse,
    Error,
    { userUuid: string; resumeFrom: string; resumeTo: string; reason?: string }
  >({
    mutationFn: ({ userUuid, resumeFrom, resumeTo, reason }) =>
      fetchAgentInsightsMemberReprocess(userUuid, resumeFrom, resumeTo, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentInsightsMemberSyncHealth'],
      });
    },
  });
}

// --- Agent Insights Overview Bundle -----------------------------------------
// Replaces the 9 standalone overview/* queries above (team, usage, teamHealth,
// overviewSpend, maturity, + the 4 the health composite folded in) with one
// shared request per (org, window). staleTime matches the smallest value any
// of the replaced hooks used (5 min), so this doesn't cache staler than before.

// The key comes from lib/queryKeys/agentInsights.ts so the server prefetch can
// share it. It cannot import from this file: this module imports useEffect, so
// pulling any binding from it into a Server Component is a build error.
const overviewBundleQueryOptions = (
  orgUuid: string,
  days?: number | null,
  enabled = true,
  teamUuid?: string | null
) => ({
  queryKey: agentInsightsOverviewBundleKey(orgUuid, days, teamUuid),
  queryFn: () => fetchAgentInsightsOverviewBundle(orgUuid, days, teamUuid),
  enabled: enabled && !!orgUuid,
  staleTime: 5 * 60 * 1000,
});

export function useAgentInsightsOverviewBundle(
  orgUuid: string,
  days?: number | null,
  enabled = true
) {
  const teamUuid = useAgentInsightsTeamUuid();
  return useQuery<AgentInsightsOverviewBundleResponse>(
    overviewBundleQueryOptions(orgUuid, days, enabled, teamUuid)
  );
}

/**
 * Dispatch the Overview bundle early and answer the page's two gates from it.
 *
 * The tab used to resolve its gates strictly in series — `onboardingStatus`,
 * then `dataStatus` gated on that, and only once both had landed did the
 * sections mount and dispatch the bundle. Measured across 277 production
 * loads, the bundle did not even leave the browser until a mean of 2.23s
 * (p90 3.44s) after the page's first request, with `data-status` alone
 * costing ~760ms of server time on the critical path.
 *
 * The backend already returns both gates inside the bundle. Calling this hook
 * above the tab's early returns is what moves the bundle off the back of that
 * chain: it goes out as soon as onboarding is known complete, in parallel with
 * the transcript gate rather than behind it.
 *
 * Seeding is the secondary benefit, and it does NOT eliminate the cold-load
 * `data-status` request. The write happens in an effect, after the bundle
 * resolves, whereas the tab's `useAgentInsightsDataStatus` is enabled during
 * the same render and dispatches immediately. Priming pays off on the warm
 * paths — a window change, a tab return, a remount after that query's 55s
 * staleTime lapsed — and gives the gate an answer when its own fetch fails,
 * since it is configured not to retry.
 *
 * Gating the standalone query on this one to save that request would put the
 * transcript gate behind a p50 3.3s bundle, re-serializing what this exists to
 * unserialize. One parallel request is the cheaper end of that trade.
 *
 * Seeding is one-shot per key — it only writes when the target query has no
 * data yet. `useAgentInsightsDataStatus` polls itself while an org is waiting
 * on its first sync, and a later bundle refetch must not overwrite a fresher
 * poll result with the gate value it happened to be carrying.
 *
 * Args:
 *   orgUuid: tenant scope; nothing dispatches until this is non-empty.
 *   days: the window the sections will request, so this warms THEIR cache
 *     entry rather than a neighbouring one.
 *   enabled: gate for the bundle request itself (the tab passes its
 *     onboarding-complete flag, so a not-yet-onboarded org is not charged for
 *     a dashboard it cannot see).
 *
 * Returns:
 *   Nothing. The effect is on the React Query cache; consumers keep reading
 *   through their existing hooks.
 */
export function useAgentInsightsOverviewBundlePrimedGates(
  orgUuid: string,
  days?: number | null,
  enabled = true
) {
  const queryClient = useQueryClient();
  const teamUuid = useAgentInsightsTeamUuid();
  const { data } = useAgentInsightsOverviewBundle(orgUuid, days, enabled);
  // The gates are org-level answers. A team-scoped bundle's gates describe
  // that team only, so never seed the org-wide gate caches from it.
  const gates = teamUuid ? undefined : data?.gates;

  useEffect(() => {
    if (!orgUuid) return;

    // A failed gate read arrives as null. Seeding that would tell the page
    // "not onboarded" / "no transcript data" on the strength of an RDS error,
    // so leave the cache alone and let the standalone endpoint answer.
    if (gates?.onboardingStatus) {
      const key = ['agentInsightsOnboardingStatus', orgUuid];
      if (queryClient.getQueryData(key) === undefined) {
        queryClient.setQueryData(key, {
          success: true,
          completed: gates.onboardingStatus.completed,
        });
      }
    }

    if (gates?.dataStatus) {
      const key = ['agentInsightsDataStatus', orgUuid];
      if (queryClient.getQueryData(key) === undefined) {
        queryClient.setQueryData(key, { success: true, ...gates.dataStatus });
      }
    }
  }, [orgUuid, gates, queryClient]);
}

/** Fetch a page section independently; concurrent consumers share its query key. */
export function useIndependentOverviewSection<
  K extends 'team' | 'spend' | 'maturity',
>(orgUuid: string, days: number | null | undefined, key: K, enabled = true) {
  const teamUuid = useAgentInsightsTeamUuid();
  return useQuery({
    queryKey: [
      'agentInsightsOverviewSection',
      orgUuid,
      days ?? null,
      key,
      teamUuid,
    ],
    queryFn: () =>
      fetchAgentInsightsOverviewSection(orgUuid, days, key, teamUuid),
    enabled: enabled && !!orgUuid,
    staleTime: 5 * 60 * 1000,
  });
}

/** Members grid roster + proficiency band, one call (`agentInsights/members/`)
 * replacing that grid's separate `team` + `maturity` independent-section
 * reads. Concurrent consumers share this query key. */
export const agentInsightsMembersQueryOptions = (
  orgUuid: string,
  days: number | null | undefined,
  teamUuid: string | null
) =>
  queryOptions({
    queryKey: agentInsightsMembersKey(orgUuid, days, teamUuid),
    queryFn: () => fetchAgentInsightsMembers(days, teamUuid),
    staleTime: 5 * 60 * 1000,
  });

export function useAgentInsightsMembers(
  orgUuid: string,
  days: number | null | undefined,
  enabled = true
) {
  const teamUuid = useAgentInsightsTeamUuid();
  return useQuery({
    ...agentInsightsMembersQueryOptions(orgUuid, days, teamUuid),
    enabled: enabled && !!orgUuid,
  });
}

/** Thrown from a bundle selector when the backend listed a section the
 * selector reads in `failedSections`. Throwing from `select` puts that one
 * observer in React Query's error state (`isError`, `error`) without touching
 * the shared bundle query or any sibling section's observer. */
export class OverviewSectionFailedError extends Error {}

/** `pick(bundle)`, or throw when any of `keys` failed to build server-side. */
function selectUnlessFailed<T>(
  bundle: AgentInsightsOverviewBundleResponse,
  keys: readonly (keyof AgentInsightsOverviewBundleSections)[],
  pick: (bundle: AgentInsightsOverviewBundleResponse) => T
): T {
  const failed = keys.filter((k) => bundle.failedSections?.includes(k));
  if (failed.length)
    throw new OverviewSectionFailedError(
      `Overview section failed: ${failed.join(', ')}`
    );
  return pick(bundle);
}

/** On a `select` error React Query keeps `data` at the observer's LAST good
 * selection, which after an org or window switch is a different org's or
 * window's section. Blank it, so a failed section can only ever read as
 * failed. Spreads only in that case: the healthy path keeps the tracked
 * result and its render-on-used-props optimisation. */
function withoutStaleSelection<R extends { error: unknown }>(result: R): R {
  return result.error instanceof OverviewSectionFailedError
    ? ({ ...result, data: undefined } as R)
    : result;
}

/** Per-section selector on the shared bundle so a tile only re-renders when
 * ITS section's reference changes, not on every bundle refetch. `enabled`
 * lets callers gate on more than `orgUuid` (e.g. an onboarding-readiness
 * check), matching the hooks this replaces.
 *
 * A section the backend failed to build (listed in `failedSections`) comes
 * back as `isError` with `data` undefined, so a broken backend no longer
 * reads as "no data yet". A `null` section that is NOT listed (an older
 * backend without `failedSections`) still maps to `undefined`, never `null`. */
export function useBundleSection<
  K extends keyof AgentInsightsOverviewBundleSections,
>(orgUuid: string, days: number | null | undefined, key: K, enabled = true) {
  const teamUuid = useAgentInsightsTeamUuid();
  return withoutStaleSelection(
    useQuery<
      AgentInsightsOverviewBundleResponse,
      Error,
      NonNullable<AgentInsightsOverviewBundleSections[K]> | undefined
    >({
      ...overviewBundleQueryOptions(orgUuid, days, enabled, teamUuid),
      select: (bundle) =>
        selectUnlessFailed(bundle, [key], (b) => b.sections[key] ?? undefined),
    })
  );
}

/** Legacy Team Health tile composite (health + efficiency + spend +
 * driving-style), reconstructed from the bundle in the selector so it
 * replaces the old 4-endpoint `useAgentInsightsTeamHealth` transparently. */
export function useTeamHealthBundleSection(
  orgUuid: string,
  days?: number | null,
  enabled = true
) {
  const teamUuid = useAgentInsightsTeamUuid();
  return withoutStaleSelection(
    useQuery<
      AgentInsightsOverviewBundleResponse,
      Error,
      AgentInsightsTeamHealthResponse | undefined
    >({
      ...overviewBundleQueryOptions(orgUuid, days, enabled, teamUuid),
      select: (bundle) =>
        selectUnlessFailed(
          bundle,
          ['health', 'efficiency', 'spend', 'drivingStyle'],
          (b) => composeTeamHealthFromBundle(b.sections)
        ),
    })
  );
}

/** Legacy {org, developers, coaching} maturity shape, reconstructed from the
 * bundle's raw `maturity` section in place of the old inline
 * `fetchAgentInsightsMaturity` useQuery calls. */
export function useMaturityBundleSection(
  orgUuid: string,
  days?: number | null,
  enabled = true
) {
  const teamUuid = useAgentInsightsTeamUuid();
  return withoutStaleSelection(
    useQuery<
      AgentInsightsOverviewBundleResponse,
      Error,
      AgentMaturityResponse | undefined
    >({
      ...overviewBundleQueryOptions(orgUuid, days, enabled, teamUuid),
      select: (bundle) =>
        selectUnlessFailed(bundle, ['maturity'], (b) =>
          composeMaturityFromBundle(b.sections.maturity)
        ),
    })
  );
}

export function useAgentInsightsTeamConnectionStatus(
  orgUuid: string,
  enabled = true
) {
  /** Slim CLI connection-status roster for the Agent Insights onboarding
   * wizard — who has synced the CLI vs not. Cheaper than `useAgentInsightsTeam`
   * (no spend/efficiency rollup). Org is resolved server-side from the admin. */
  return useQuery<AgentInsightsTeamStatusResponse>({
    queryKey: ['agentInsightsTeamStatus', orgUuid],
    queryFn: () => fetchAgentInsightsTeamStatus(),
    enabled: enabled && !!orgUuid,
    staleTime: 60 * 1000,
  });
}

export function useAgentInsightsOnboardingStatus(
  orgUuid: string,
  enabled = true
) {
  /** Per-org latch: has an admin completed the Agent Insights onboarding?
   * Drives the tab's decision to show the wizard vs the insights body.
   *
   * Keyed by orgUuid so a soft org switch (no page reload) doesn't leak one
   * org's latch to another. Warmed from the team-insight layout so it
   * dispatches ahead of the page's heavier orgUuid-gated requests. */
  return useQuery<{ success: boolean; completed: boolean }>({
    queryKey: ['agentInsightsOnboardingStatus', orgUuid],
    queryFn: () => fetchAgentInsightsOnboardingStatus(),
    enabled: enabled && !!orgUuid,
    // Latch is immutable within a session for a given org (flips once when an
    // admin completes onboarding, which the Zustand overlay reflects
    // instantly). Don't refetch — refreshed on reload / when orgUuid changes.
    staleTime: Infinity,
  });
}

export function useAgentInsightsDataStatus(orgUuid: string, enabled = true) {
  /** Whether transcript-grade data exists behind the org's Agent Insights.
   *
   * Drives the connect-transcripts gate: a metrics-only org sees a "sync
   * with the latest CLI" page instead of an empty dashboard. Polls while
   * gated so the page lights up within a minute of the org's first
   * transcript sync landing — no reload needed.
   *
   * The gate is OPTIONAL decoration over the dashboard, so failure handling
   * fails open fast and stays quiet: no retries (a 404 before the backend
   * ships must not hold the tab in its loader), and no polling after an
   * error (the endpoint being absent won't change a minute from now). */
  return useQuery<AgentInsightsDataStatusResponse>({
    queryKey: ['agentInsightsDataStatus', orgUuid],
    queryFn: () => fetchAgentInsightsDataStatus(),
    enabled: enabled && !!orgUuid,
    retry: false,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return query.state.data?.hasTranscriptData ? false : 60_000;
    },
    staleTime: 55_000,
  });
}

export function useModelRouterOnboardingStatus(
  orgUuid: string,
  enabled = true
) {
  /** Per-org latch: has an admin completed the Model Router onboarding?
   * Drives the /model-router route's decision to show the onboarding flow vs
   * hand off to the Agent Insights dashboard. Same shape and caching as the
   * Agent Insights latch above — keyed by orgUuid, immutable within a session. */
  return useQuery<{ success: boolean; completed: boolean }>({
    queryKey: ['modelRouterOnboardingStatus', orgUuid],
    queryFn: () => fetchModelRouterOnboardingStatus(),
    enabled: enabled && !!orgUuid,
    staleTime: Infinity,
  });
}

/** Shared cache policy for Model Router dashboard reads (sidebar remount). */
const MODEL_ROUTER_QUERY_CACHE = {
  staleTime: 5 * 60 * 1000,
} as const;

/** A permission failure (backend PR #5488's `access_denied`) will not resolve
 * on a second attempt, so skip the app-wide one retry (queryClient.ts) for it
 * and keep it for every other (transient) failure. */
const MODEL_ROUTER_RETRY = {
  retry: (failureCount: number, error: unknown) =>
    !isAccessDenied(error) && failureCount < 1,
} as const;

export function useModelRouterDashboard(
  orgUuid: string,
  opts: {
    range?: ModelRouterRange;
    granularity?: ModelRouterGranularity;
    userUuid?: string | null;
    teamUuid?: string | null;
  } = {},
  enabled = true
) {
  /** Model Router Overview data for the selected range + granularity + scope.
   * Keeps the previous data while the new query loads, so flipping any control
   * doesn't flash a skeleton. Cache key includes every param so switching
   * back to a prior combo is instant. */
  const range = opts.range ?? '1m';
  const granularity = opts.granularity ?? 'day';
  const userUuid = opts.userUuid ?? null;
  const teamUuid = opts.teamUuid ?? null;
  const { orgResolutionDone } = useUserLogged();
  const query = useQuery({
    queryKey: [
      'modelRouterDashboard',
      orgUuid,
      range,
      granularity,
      userUuid,
      teamUuid,
    ],
    queryFn: () =>
      fetchModelRouterDashboard({
        range,
        granularity,
        userUuid,
        teamUuid,
      }),
    enabled: enabled && !!orgUuid,
    ...MODEL_ROUTER_QUERY_CACHE,
    ...MODEL_ROUTER_RETRY,
    placeholderData: keepPreviousData,
  });
  return {
    data: query.data,
    error: query.error,
    isError: query.isError,
    isPlaceholderData: query.isPlaceholderData,
    refetch: query.refetch,
    // Disabled while the org resolves is pending-not-fetching, so isLoading
    // is false with no data yet (FE-33); count the unresolved org as loading
    // too, or the dashboard paints an empty state before data can exist.
    isLoading: query.isLoading || (enabled && !orgUuid && !orgResolutionDone),
  };
}

export function useModelRouterSettings(orgUuid: string, enabled = true) {
  /** Model Router Settings (wallet balance + transactions). */
  const { orgResolutionDone } = useUserLogged();
  const query = useQuery({
    queryKey: ['modelRouterSettings', orgUuid],
    queryFn: () => fetchModelRouterSettings(),
    enabled: enabled && !!orgUuid,
    ...MODEL_ROUTER_QUERY_CACHE,
  });
  return {
    data: query.data,
    isError: query.isError,
    refetch: query.refetch,
    // Disabled while the org resolves is pending-not-fetching, so isLoading
    // is false with no data yet (FE-33); count the unresolved org as loading
    // too, or Settings shows a load failure before it ever failed.
    isLoading: query.isLoading || (enabled && !orgUuid && !orgResolutionDone),
  };
}

export function useModelRouterSessions(
  orgUuid: string,
  enabled = true,
  range: ModelRouterRange = '1m'
) {
  /** Model Router Sessions table — routed sessions from the billing ledger (newest
   * first): developer, token totals, real billed cost, and savings vs Opus. */
  const { orgResolutionDone } = useUserLogged();
  const query = useQuery({
    // `range` is in the key: without it a range switch reads the cached
    // response for the previous window and the tab silently disagrees with
    // the header it sits behind.
    queryKey: ['modelRouterSessions', orgUuid, range],
    queryFn: () => fetchModelRouterSessions(range),
    enabled: enabled && !!orgUuid,
    ...MODEL_ROUTER_QUERY_CACHE,
    ...MODEL_ROUTER_RETRY,
  });
  return {
    data: query.data,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    // Disabled while the org resolves is pending-not-fetching, so isLoading
    // is false with no data yet (FE-33); count the unresolved org as loading
    // too, or the table paints an empty state before data can exist.
    isLoading: query.isLoading || (enabled && !orgUuid && !orgResolutionDone),
  };
}

export function useModelRouterMembers(
  orgUuid: string,
  enabled = true,
  range: ModelRouterRange = '1m'
) {
  /** Per-developer savings leaderboard from the billing ledger. */
  const { orgResolutionDone } = useUserLogged();
  const query = useQuery({
    queryKey: ['modelRouterMembers', orgUuid, range],
    queryFn: () => fetchModelRouterMembers(range),
    enabled: enabled && !!orgUuid,
    ...MODEL_ROUTER_QUERY_CACHE,
    ...MODEL_ROUTER_RETRY,
  });
  return {
    data: query.data,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    // Disabled while the org resolves is pending-not-fetching, so isLoading
    // is false with no data yet (FE-33); count the unresolved org as loading
    // too, or the leaderboard paints an empty state before data can exist.
    isLoading: query.isLoading || (enabled && !orgUuid && !orgResolutionDone),
  };
}

export function useModelRouterMemberDetail(
  userUuid: string | null,
  enabled = true,
  range: ModelRouterRange = '1m'
) {
  /** One developer's savings-over-time + totals. Lazy: only when a row is opened.
   * orgUuid is baked into the key so the same userUuid seen from two different
   * orgs (e.g. an admin bouncing between org contexts) never returns cached
   * data from the wrong tenant. */
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  return useQuery({
    // `range` belongs in the key for the same reason as the list: without it,
    // reopening a row after a window change replays the previous window's
    // response and the drill-down disagrees with the row that opened it.
    queryKey: ['modelRouterMemberDetail', orgUuid, userUuid, range],
    queryFn: () => fetchModelRouterMemberDetail(userUuid as string, range),
    enabled: enabled && !!userUuid,
    ...MODEL_ROUTER_QUERY_CACHE,
    ...MODEL_ROUTER_RETRY,
  });
}

export function useAgentInsightsCoaching(userUuid: string, enabled = true) {
  /** React Query hook for a developer's metric-grounded coaching. Pass the
   * member's userUuid (admin view); self-scoped if it matches the caller.
   * Lazy: enable only when the row is expanded so we don't fan out one call
   * per member up front. */
  return useQuery<AgentInsightsCoachingResponse>({
    queryKey: ['agentInsightsCoaching', userUuid],
    queryFn: () => fetchAgentInsightsCoaching(userUuid),
    enabled: enabled && !!userUuid,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAgentSessions(
  orgUuid: string,
  userUuid: string | null,
  days?: number | null,
  options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    cursor?: string;
  },
  enabled = true
) {
  const requestedPage = options?.page ?? 1;
  const requestedCursor = options?.cursor ?? null;
  const requestedSearch = options?.search?.trim() ?? '';
  return useQuery<
    AgentSessionsResponse & {
      requestSnapshot: {
        cursor: string | null;
        page: number;
        search: string;
      };
    }
  >({
    queryKey: [
      'agentInsightsSessions',
      orgUuid,
      userUuid,
      days ?? 'all',
      requestedPage,
      options?.pageSize ?? 200,
      requestedSearch,
      requestedCursor ?? 'first',
    ],
    queryFn: async () => {
      const response = await fetchAgentSessions({
        userUuid: userUuid as string,
        days: days ?? null,
        page: requestedCursor ? undefined : options?.page,
        pageSize: options?.pageSize,
        search: requestedSearch,
        cursor: requestedCursor ?? undefined,
      });
      return {
        ...response,
        requestSnapshot: {
          cursor: requestedCursor,
          page: requestedPage,
          search: requestedSearch,
        },
      };
    },
    enabled: enabled && !!orgUuid && !!userUuid,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAgentSessionDetail(
  orgUuid: string,
  sessionId: string | null,
  enabled = true
) {
  return useQuery<{
    success: boolean;
    session: AgentSessionDetail | null;
  }>({
    queryKey: ['agentInsightsSessionDetail', orgUuid, sessionId],
    queryFn: () => fetchAgentSessionDetail(sessionId as string),
    enabled: enabled && !!orgUuid && !!sessionId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgentSessionActivityMap(
  orgUuid: string,
  params: AgentSessionActivityMapParams,
  enabled = true
) {
  /** React Query hook for the Agent Session Activity Map heatmap. Served by
   * agentInsights/sessionActivityMap/ — keyed by every filter/search/page so
   * each combination caches independently, with placeholderData so paging or
   * filtering never flashes a skeleton. */
  const teamUuid = useAgentInsightsTeamUuid();
  return useQuery<AgentSessionActivityMapResponse>({
    queryKey: [
      'agentSessionActivityMap',
      orgUuid,
      params.groupBy,
      params.repository,
      params.tool,
      params.search,
      params.page,
      teamUuid,
    ],
    queryFn: () => fetchAgentSessionActivityMap({ ...params, teamUuid }),
    enabled: enabled && !!orgUuid,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useAgentInsightsFindings(orgUuid: string, enabled = true) {
  /** React Query hook for the guardrailed Agent Insights findings feed — shared
   * by the Agents Summary card and other findings surfaces on the tab. */
  return useQuery<Awaited<ReturnType<typeof fetchAgentInsightsFindings>>>({
    queryKey: ['agentInsightsFindings', orgUuid],
    queryFn: fetchAgentInsightsFindings,
    enabled: enabled && !!orgUuid,
    staleTime: 10 * 60 * 1000,
  });
}

export function useModelQualityComparison(
  orgUuid: string,
  params: ModelQualityComparisonParams = {},
  enabled = true
) {
  /** Review comments grouped by AUTHORING model (PR<->model attribution) for
   * the Model Quality dashboard. Keeps previous data while a new date-range
   * loads, so changing the range doesn't flash a skeleton.
   *
   * No `refetchInterval`. One call to `modelQualityComparison/` runs five
   * aggregates server side, and this hook has three consumers (this page, the
   * Model Router ROI tab, the admin home Model Burn widget), so a 60s poll re-ran
   * that work every minute on any open tab for no user-visible gain. Window-focus
   * refetch covers the "a PR was just attributed" case and, unlike an interval,
   * only fires once the row is stale. */
  return useQuery<ModelQualityComparisonResponse>({
    queryKey: ['modelQualityComparison', orgUuid, params.start, params.end],
    queryFn: () => fetchModelQualityComparison(params),
    enabled: enabled && !!orgUuid,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true, // refresh on return so a just-attributed PR shows up
    placeholderData: keepPreviousData,
    ...MODEL_ROUTER_RETRY,
  });
}

// --- Session Explorer (ENT-4777 / SE-10) ------------------------------------

export const sessionExplorerStatusKey = (orgUuid: string, userUuid: string) =>
  ['sessionExplorerStatus', orgUuid, userUuid] as const;

/** Gate for the member page's Overview / Session Explorer tabs. Fails closed:
 * any error reads as "not available" in the caller. */
export function useSessionExplorerStatus(
  orgUuid: string,
  userUuid: string | null
) {
  return useQuery({
    queryKey: sessionExplorerStatusKey(orgUuid, userUuid ?? ''),
    queryFn: () => fetchSessionExplorerStatus(userUuid as string),
    enabled: !!orgUuid && !!userUuid,
    staleTime: 3 * 60 * 1000,
    retry: false,
  });
}

const SESSION_EXPLORER_PAGE_SIZE = 50;

export function useSessionExplorerSessions(
  orgUuid: string,
  userUuid: string | null,
  search: string,
  range: { from?: string; to?: string } = {},
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [
      'sessionExplorerSessions',
      orgUuid,
      userUuid,
      search,
      range.from ?? null,
      range.to ?? null,
    ],
    queryFn: ({ pageParam }) =>
      fetchSessionExplorerSessions({
        userUuid: userUuid as string,
        search: search || undefined,
        from: range.from,
        to: range.to,
        pageSize: SESSION_EXPLORER_PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    enabled: enabled && !!orgUuid && !!userUuid,
    staleTime: 60 * 1000,
    // Search is in the key: keep the old list up while a new term loads so
    // the filter input is never unmounted mid-typing.
    placeholderData: keepPreviousData,
  });
}

export const sessionExplorerMapBatchKey = (
  orgUuid: string,
  userUuid: string,
  ids: readonly string[]
) => ['sessionExplorerMapBatch', orgUuid, userUuid, ids.join(',')] as const;

export const sessionExplorerMapDocKey = (contentHash: string | null) =>
  ['sessionExplorerMap', contentHash] as const;

const NO_ANNOTATIONS: readonly SessionMapAnnotation[] = [];
const NO_PENDING_REVIEW: PendingReview | null = null;

/** Presigned URLs live 300s server side; refetch well inside that. */
const MAP_URL_STALE_MS = 60 * 1000;

/**
 * Batch-sign the selected session plus the next two in the list, load the
 * selected map now and prefetch the other two into the contentHash-keyed
 * cache (documents are immutable per hash, so they never go stale). A map
 * whose URL expired or whose download failed on the network is re-signed
 * once before the caller shows its inline error.
 */
export function useSessionExplorerMap(
  orgUuid: string,
  userUuid: string | null,
  sessionId: string | null,
  listIds: readonly string[]
) {
  const queryClient = useQueryClient();
  const user = userUuid ?? '';
  const batchRoot = ['sessionExplorerMapBatch', orgUuid, user] as const;
  const unavailable = new Set<string>();
  for (const [, data] of queryClient.getQueriesData<SessionExplorerMapBatch>({
    queryKey: batchRoot,
  })) {
    for (const m of data?.maps ?? [])
      if (!isMapFound(m)) unavailable.add(m.sessionId);
  }
  const ids = sessionId ? mapRequestIds(listIds, sessionId, unavailable) : [];
  const batchKey = sessionExplorerMapBatchKey(orgUuid, user, ids);
  const batch = useQuery({
    queryKey: batchKey,
    queryFn: () => fetchSessionExplorerMapUrls(user, ids),
    enabled: !!orgUuid && !!userUuid && ids.length > 0,
    staleTime: MAP_URL_STALE_MS,
    retry: 1,
    // Learning a neighbour has no map changes the id set; keep the answer
    // we have so the selected map does not flash back to a skeleton.
    placeholderData: keepPreviousData,
  });
  for (const m of batch.data?.maps ?? [])
    if (!isMapFound(m)) unavailable.add(m.sessionId);

  const items = batch.data?.maps ?? [];
  const selected = items.find((m) => m.sessionId === sessionId);
  const found = selected && isMapFound(selected) ? selected : null;

  const loadDoc = useCallback(
    (item: SessionExplorerMapMeta) =>
      fetchMapWithRetry(
        item.url,
        async () => {
          const fresh = await fetchSessionExplorerMapUrls(user, [
            item.sessionId,
          ]);
          const m = fresh.maps.find((x) => x.sessionId === item.sessionId);
          if (!m || !isMapFound(m)) {
            throw new SessionExplorerError(
              'sessionMap.resign not found',
              'not_found'
            );
          }
          return m.url;
        },
        fetchSessionMapDocument
      ),
    [user]
  );

  const doc = useQuery({
    queryKey: sessionExplorerMapDocKey(found?.contentHash ?? null),
    queryFn: () => loadDoc(found as SessionExplorerMapMeta),
    enabled: !!found,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  // Prefetch the neighbours without blocking the selected map.
  useEffect(() => {
    for (const m of batch.data?.maps ?? []) {
      if (m.sessionId === sessionId || !isMapFound(m)) continue;
      void queryClient.prefetchQuery({
        queryKey: sessionExplorerMapDocKey(m.contentHash),
        queryFn: () => loadDoc(m),
        staleTime: Infinity,
        gcTime: 10 * 60 * 1000,
      });
    }
  }, [batch.data, sessionId, queryClient, loadDoc]);

  // Only from the batch whose maps[] actually names this session: under
  // keepPreviousData a stale batch for other ids must not lend its notes.
  const annotations: readonly SessionMapAnnotation[] =
    (selected && sessionId ? batch.data?.annotations[sessionId] : undefined) ??
    NO_ANNOTATIONS;
  // V11: this session's PR review has not settled final yet, so an empty
  // `annotations` entry means "pending", not "nothing found".
  const reviewPending: PendingReview | null =
    (selected && sessionId
      ? batch.data?.reviewPending[sessionId]
      : undefined) ?? NO_PENDING_REVIEW;

  const notFound = !!selected && !found;
  const retry = async () => {
    await queryClient.invalidateQueries({ queryKey: batchKey });
    if (found) await doc.refetch();
  };
  return {
    data: doc.data ?? null,
    isLoading: batch.isLoading || (!!found && doc.isLoading),
    isError: batch.isError || doc.isError || notFound,
    error: notFound
      ? new SessionExplorerError('sessionMap not found', 'not_found')
      : (batch.error ?? doc.error ?? null),
    unavailable: unavailable as ReadonlySet<string>,
    /** review annotations for the selected session (`[]` when none) */
    annotations,
    /** set when this session's PR review correlations are not final yet */
    reviewPending,
    retry,
  };
}
